import dayjs from "dayjs";
import { useIntl } from "react-intl";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import style from "../AuthoredHeader/style.module.css";
import { activityStore } from "../../../../store/activity.store";
import { Activity, ActivityType, Interval } from "../../../../model/activity.model";
import { ActivityStatus } from "../../../../enum/activity.enum";
import { DATE_TIME_FORMAT } from "../../../../constant/format";
import { getActivityTypeOptions } from "../AuthoredHeader/helper";
import Intervals from "./components/Intervals";
import { debounce } from "../../../../store/utils/debounce";
import { TreeSelect } from "../../../ui/tree-select";
import { Segmented } from "../../../ui/segmented";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { Textarea } from "../../../ui/textarea";
import { TagInput } from "../../../ui/tag-input";
import { DateTimePicker } from "../../../ui/datetime-picker";

// Filter activity types to only show types matching the occurrence status
const filterActivityTypesByOccurrence = (types: ActivityType[], isOccurrence: boolean): ActivityType[] => {
  return types.filter(t => !!t.occurrence === isOccurrence);
};

interface ActivityFormValues {
  id: string;
  status: ActivityStatus;
  typeId: string;
  start?: string;
  comment: string;
  tags: string[];
  duration: number;
  intervals: Interval[];
}

export interface ActivityFormRef {
  submit: () => Promise<void>;
}

interface IActivityForm {
  activity: Activity;
  onSubmit?: (values: ActivityFormValues) => void;
}

const EditActivityForm = forwardRef<ActivityFormRef, IActivityForm>(({ activity, onSubmit }, ref) => {
  const { formatMessage } = useIntl();
  const availableActivityTags = activityStore(state => state.activityTags);
  const activitiesTypesLoading = activityStore(state => state.loading.getActivityTypes);
  const activityTypes = activityStore(state => state.activityTypes || []);
  const rewriteActivity = activityStore(state => state.rewriteActivity);

  // Determine if the activity type is an occurrence
  const isOccurrence = useMemo(() => {
    const type = activityTypes.find(t => t.id === activity.typeId);
    return !!type?.occurrence;
  }, [activityTypes, activity.typeId]);

  // Filter types to only show matching occurrence status (can't switch between occ/non-occ)
  const filteredActivityTypes = useMemo(() =>
    filterActivityTypesByOccurrence(activityTypes, isOccurrence),
    [activityTypes, isOccurrence]
  );

  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [isStartFieldDisabled, setIsStartFieldDisabled] = useState<boolean>();
  const getTagsStartPage = activityStore(state => state.getActivityTagsStartPage);
  const tagsLoading = activityStore(state => state.loading.getActivityTagsStartPage);
  const setActivityTagsParams = activityStore(state => state.setActivityTagsParams);
  const query = activityStore(state => state.activityTagsParams.query);

  const { control, handleSubmit, setValue, watch, reset } = useForm<ActivityFormValues>({
    defaultValues: {
      id: activity.id,
      status: activity.status,
      typeId: activity.typeId,
      start: activity.start,
      comment: activity.comment || '',
      tags: activity.tags || [],
      duration: activity.duration,
      intervals: activity.intervals || [],
    }
  });

  // Expose submit method via ref
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(handleFormSubmit)()
  }));

  useEffect(() => {
    reset({
      id: activity.id,
      status: activity.status,
      typeId: activity.typeId,
      start: activity.start,
      comment: activity.comment || '',
      tags: activity.tags || [],
      duration: activity.duration,
      intervals: activity.intervals || [],
    });

    setIsStartFieldDisabled(activity.status !== ActivityStatus.RUNNING);
    setIntervals(activity.intervals);
  }, [activity, reset]);

  useEffect(() => {
    !tagsLoading && getTagsStartPage();
  }, [query]);

  const handleFormSubmit = useCallback((values: ActivityFormValues) => {
    const submitFn = onSubmit || rewriteActivity;
    return submitFn(values as Activity);
  }, [onSubmit, rewriteActivity]);

  const onSearchCallback = useCallback(debounce((value: string) =>
    setActivityTagsParams({ query: value }), 500), [setActivityTagsParams]
  );

  const filterTreeNodeCallback = useCallback((inputValue: string, treeNode: any) => !inputValue
    ? true
    : treeNode.name.toLowerCase().includes(inputValue.toLowerCase()
    ), []);

  const onRadioChangeCallback = useCallback((value: string) => {
    const isActivityStatusPausedOrStopped = activity.status === ActivityStatus.STOPPED || activity.status === ActivityStatus.PAUSED;

    setIsStartFieldDisabled(false);
    setValue('status', value as ActivityStatus);

    if (isActivityStatusPausedOrStopped) {
      if (value === ActivityStatus.RUNNING) {
        const now = new Date().toISOString();
        setValue('start', now);
      } else {
        setIsStartFieldDisabled(true);
        setValue('start', undefined);
      }
    } else {
      if (value === ActivityStatus.PAUSED || value === ActivityStatus.STOPPED) {
        setIsStartFieldDisabled(true);

        const currentStart = watch('start');
        const newInterval = {
          typeId: activity.typeId,
          activityId: activity.id,
          tags: activity.tags,
          start: currentStart || activity.start,
          finish: new Date().toISOString(),
          comment: '',
        } as Interval;

        setValue('start', undefined);

        setIntervals(intervals => {
          const newIntervals = [...intervals, newInterval];
          setValue('intervals', newIntervals);
          return newIntervals;
        });
      }
    }
  }, [activity, setValue, watch]);

  return (
    <form className="space-y-4">
      <div className="hidden">
        <Controller
          name="id"
          control={control}
          render={({ field }) => <Input {...field} disabled />}
        />
      </div>

      {!isOccurrence && (
        <div className="grid grid-cols-[100px_1fr] items-center gap-4">
          <Label>{formatMessage({ id: 'STATUS' })}</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Segmented
                className="w-fit"
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  onRadioChangeCallback(value as string);
                }}
                options={[
                  { value: ActivityStatus.PAUSED, label: formatMessage({ id: ActivityStatus.PAUSED }) },
                  { value: ActivityStatus.RUNNING, label: formatMessage({ id: ActivityStatus.RUNNING }) },
                  { value: ActivityStatus.STOPPED, label: formatMessage({ id: ActivityStatus.STOPPED }) },
                ]}
              />
            )}
          />
        </div>
      )}

      <div className="grid grid-cols-[100px_1fr] items-center gap-4">
        <Label>{formatMessage({ id: 'TYPE_ID' })}</Label>
        <Controller
          name="typeId"
          control={control}
          render={({ field }) => (
            <TreeSelect
              className={style.select}
              filterTreeNode={filterTreeNodeCallback}
              loading={activitiesTypesLoading}
              showSearch
              onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
              value={field.value}
              treeData={getActivityTypeOptions(filteredActivityTypes)}
              placeholder={formatMessage({ id: 'SELECT_ACTIVITY' })}
            />
          )}
        />
      </div>

      {!isStartFieldDisabled && (
        <div className="grid grid-cols-[100px_1fr] items-center gap-4">
          <Label>{formatMessage({ id: 'START' })}</Label>
          <Controller
            name="start"
            control={control}
            render={({ field }) => (
              <DateTimePicker
                disabled={isStartFieldDisabled}
                showTime
                value={field.value ? dayjs(field.value) : undefined}
                onChange={(date) => field.onChange(date?.toISOString())}
              />
            )}
          />
        </div>
      )}

      <div className="grid grid-cols-[100px_1fr] items-start gap-4">
        <Label className="pt-1.5">{formatMessage({ id: 'COMMENT' })}</Label>
        <Controller
          name="comment"
          control={control}
          render={({ field }) => <Textarea {...field} />}
        />
      </div>

      <div className="grid grid-cols-[100px_1fr] items-center gap-4">
        <Label>{formatMessage({ id: 'TAGS' })}</Label>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <TagInput
              allowClear
              showSearch
              value={field.value}
              onChange={field.onChange}
              options={availableActivityTags?.map(tag => ({ value: tag, label: tag }))}
              loading={activitiesTypesLoading}
              onSearch={onSearchCallback}
              placeholder={formatMessage({ id: 'SELECT_TAGS' })}
            />
          )}
        />
      </div>

      <Controller
        name="duration"
        control={control}
        render={() => <input type="hidden" />}
      />

      {isOccurrence ? (
        // Occurrence: single Time field (no intervals UI)
        <div className="grid grid-cols-[100px_1fr] items-center gap-4">
          <Label>{formatMessage({ id: 'TIME' })}</Label>
          <DateTimePicker
            showTime
            value={intervals[0] ? dayjs(intervals[0].start) : undefined}
            onChange={(date) => {
              if (date) {
                const isoDate = date.toISOString();
                const updatedInterval = {
                  ...intervals[0],
                  start: isoDate,
                  finish: isoDate,
                };
                setIntervals([updatedInterval]);
                setValue('intervals', [updatedInterval]);
              }
            }}
          />
        </div>
      ) : (
        <Intervals
          activity={activity}
          intervals={intervals}
          setValue={setValue}
          watch={watch}
          setIntervals={setIntervals}
        />
      )}
    </form>
  );
});

EditActivityForm.displayName = 'EditActivityForm';

export default EditActivityForm;
